import { useState, useEffect, useCallback, useRef } from "react";
import { ServiceRow } from "../../logicBuilder/NewLogicModal";
import { getLogicBuilderServiceById } from "../../../../../common/network/Api";

interface UseServicesFetcherProps {
  serviceRows: ServiceRow[];
  services: any[];
  onServicesDataChange?: (services: any[]) => void;
}

interface UseServicesFetcherReturn {
  isLoading: boolean;
  error: string | null;
}

export const useServicesFetcher = ({
  serviceRows,
  services,
  onServicesDataChange,
}: UseServicesFetcherProps): UseServicesFetcherReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep a ref to latest services to avoid stale closures
  const servicesRef = useRef<any[]>(services);
  servicesRef.current = services;

  // Helper function to initialize default units for attributes
  const initializeAttributeDefaults = useCallback((services: any[]) => {
    return services.map((service) => {
      if (!service.attributes || service.attributes.length === 0) {
        return service;
      }

      const updatedAttributes = service.attributes.map((attribute: any) => {
        // Skip if unitValue is already set
        if (
          attribute.unitValue &&
          attribute.unitValue.trim() !== "" &&
          attribute.unitValue.trim() !== "None"
        ) {
          return attribute;
        }

        // For type 1 (range with unitList): use defaultUnit if available in unitList
        if (
          attribute.type === 1 &&
          attribute.defaultUnit &&
          attribute.defaultUnit.trim() !== "" &&
          attribute.unitList &&
          attribute.unitList.includes(attribute.defaultUnit.trim())
        ) {
          const trimmedUnit = attribute.defaultUnit.trim();
          return {
            ...attribute,
            unitValue: trimmedUnit,
          };
        }

        // For type 3 (single unit): use attribute.unit
        if (
          attribute.type === 3 &&
          attribute.unit &&
          attribute.unit.trim() !== ""
        ) {
          const trimmedUnit = attribute.unit.trim();
          return {
            ...attribute,
            unitValue: trimmedUnit,
          };
        }

        return attribute;
      });

      return {
        ...service,
        attributes: updatedAttributes,
      };
    });
  }, []);

  useEffect(() => {
    const fetchServicesData = async () => {
      const serviceIds = serviceRows
        .map((row) => row.selectedServiceId)
        .filter((id): id is string => id !== undefined && id !== null);

      if (serviceIds.length === 0) {
        onServicesDataChange?.([]);
        return;
      }

      // Get IDs that are already in services (check both id and userServiceID)
      const existingServiceIds = servicesRef.current.map(
        (s) => s.userServiceID || s.id
      );

      // Check for removals: services in services but not in serviceIds
      const removedServiceIds = existingServiceIds.filter(
        (id) => !serviceIds.includes(id)
      );

      if (removedServiceIds.length > 0) {
        const filteredData = servicesRef.current.filter(
          (service) =>
            !removedServiceIds.includes(service.userServiceID || service.id)
        );
        onServicesDataChange?.(filteredData);
      }

      // Check for additions: services in serviceIds but not in services
      const servicesToFetch = serviceIds.filter(
        (id) => !existingServiceIds.includes(id)
      );

      // If no new services to fetch, we're done
      if (servicesToFetch.length === 0) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getLogicBuilderServiceById(servicesToFetch);

        // Initialize default units for newly fetched services
        const servicesWithDefaults = initializeAttributeDefaults(
          response || []
        );

        // Associate each requested id with its response object BY ID, not by
        // array index — the backend does not return the services in the order
        // they were requested, so an index-based pairing scrambles which
        // service gets which `userServiceID` (and thus its attributes on save).
        // Each response object echoes its own `userServiceID` (fallback `id`),
        // so match on that and keep the request/serviceRows order.
        const fetched = servicesToFetch.map((reqId, index) => {
          const match = servicesWithDefaults.find(
            (s: any) => (s.userServiceID ?? s.id) === reqId
          );
          // Defensive: if a response object can't be matched by id, fall back
          // to the positional element so a service is never dropped.
          const base = match ?? servicesWithDefaults[index];
          return { ...base, userServiceID: reqId };
        });

        // Merge new services at the bottom of existing ones
        const mergedData = [...servicesRef.current, ...fetched];
        onServicesDataChange?.(mergedData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch services"
        );
        // Don't clear existing data on error, keep what we have
      } finally {
        setIsLoading(false);
      }
    };

    fetchServicesData();
  }, [serviceRows, onServicesDataChange, initializeAttributeDefaults]);

  return { isLoading, error };
};
